"use client";

import { useState } from "react";
import Image from "next/image";
import { Play } from "lucide-react";
import type { MediaType, TrailerOption } from "@/lib/api";
import { useCountry } from "@/lib/country-store";
import { useLocale, useMessages } from "@/lib/i18n/client";
import { pickTrailer } from "@/lib/movies";
import { track } from "@/lib/tracking/track";
import { TRAILER_PLAY_EVENT } from "./movie-soundtrack";
import messages from "./movie-trailer.i18n";

// Trailer de YouTube en "fachada": hasta que la persona toca ▶ solo se
// muestra la imagen de TMDB que ya usa la ficha — no se carga nada de
// YouTube/Google (ni la miniatura). Al tocar, el reproductor se carga desde
// youtube-nocookie (sin cookies hasta reproducir) y la banda sonora se pausa.
export function MovieTrailer({
  trailers,
  imageUrl,
  title,
  movieId,
  mediaType,
}: {
  trailers: TrailerOption[];
  imageUrl?: string;
  title: string;
  movieId: number;
  mediaType: MediaType;
}) {
  const t = useMessages(messages);
  const lang = useLocale();
  // El país del selector global decide latino vs España (ver pickTrailer).
  // Antes de leerlo vale Chile; la imagen de la fachada es la misma, así que
  // no se nota el cambio.
  const { country } = useCountry();
  const [open, setOpen] = useState(false);
  const trailer = pickTrailer(trailers, lang, country);
  if (!trailer) return null;

  const start = () => {
    window.dispatchEvent(new Event(TRAILER_PLAY_EVENT));
    track("trailer_play", { tmdb_id: movieId, media_type: mediaType });
    setOpen(true);
  };

  return (
    <section className="mt-8 max-w-2xl">
      <h2 className="mb-3 text-lg font-semibold tracking-tight">{t.heading}</h2>
      <div className="relative aspect-video overflow-hidden rounded-xl border border-border bg-black">
        {open ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(trailer.youtubeKey)}?autoplay=1&rel=0`}
            title={t.play(title)}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={start}
            aria-label={t.play(title)}
            className="group absolute inset-0 flex cursor-pointer items-center justify-center"
          >
            {imageUrl && (
              <Image src={imageUrl} alt="" fill sizes="(max-width: 672px) 100vw, 672px" className="object-cover opacity-70" />
            )}
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition group-hover:scale-110">
              <Play className="ml-1 h-7 w-7 fill-current" />
            </span>
          </button>
        )}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{t.note}</p>
    </section>
  );
}
